import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {PersonIcon} from '@primer/octicons-react'
import {Fragment} from 'react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import styles from './assignees.module.css'
import {AssignmentEventAssignee} from './AssignmentEventAssignee'
import type {AssignedEventNode} from './issue-viewer-view-query-types'
import {IssueEventRationale} from './IssueEventRationale'
import type {RolledupAssignedEventProps} from './RolledupAssignedEvent'
import {RolledupAssignedEvent} from './RolledupAssignedEvent'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(timeline pagination): widens nullable fields so the still-Relay parent data is assignable.
// Remove when the timeline pagination fragments leave Relay.
export type AssignedEventFragment = RelayCompat<
  Pick<AssignedEventNode, 'databaseId' | 'createdAt' | 'actor' | 'assignee' | 'intent'>
>

type AssignedEventProps = {
  queryRef: AssignedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: RolledupAssignedEventProps['rollupGroup']
}

export function AssignedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: AssignedEventProps): React.ReactElement {
  const {actor, createdAt, assignee, databaseId} = queryRef
  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PersonIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupAssignedEvent rollupGroup={rollupGroup} />
        ) : (
          <AddedAssigneesRendering
            queryRefs={[queryRef]}
            selfAssigned={actor?.login === assignee?.login}
            rollup={false}
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const AddedAssigneesRendering = ({
  queryRefs,
  selfAssigned,
  rollup,
}: {
  queryRefs: AssignedEventFragment[]
  selfAssigned: boolean
  rollup: boolean
}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {!selfAssigned && `${LABELS.timeline.assigned} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalAddedAssigneesRendering
            queryRef={queryRef}
            rollup={rollup}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedAssigneesRendering = ({
  queryRef,
  rollup,
  first,
  last,
}: {
  queryRef: AssignedEventFragment
  rollup: boolean
  first: boolean
  last: boolean
}) => {
  const {assignee, actor, intent, databaseId} = queryRef
  return (
    <div className={styles.assigneeEventContainer}>
      {actor?.login === assignee?.login && !rollup ? (
        <span>{LABELS.timeline.selfAssignedThis}</span>
      ) : (
        <>
          {!first && !last && <div className={styles.assigneeMarginRight}>,</div>}
          {!first && last && <div className={styles.assigneeMarginHorizontal}>{LABELS.timeline.and}</div>}
          <AssignmentEventAssignee assignee={assignee} />
        </>
      )}
      {intent?.rationale && (
        <IssueEventRationale
          rationale={intent.rationale}
          issueEventDatabaseId={String(databaseId)}
          target={assignee?.login}
          analyticsAction="ISSUE_ASSIGNEE_ADDED"
        />
      )}
    </div>
  )
}
